import { useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { ApplicationResolution, ApplicationStatus } from "model/Application";
import type { CompanyGig } from "model/Gig";
import { Api } from "services/api/api.service";
import { toApplicationStatusUpdate } from "web-apps/company/components/applications/applicationStatus.logic";
import type {
  TeamPublicApplicationAction,
  TeamPublicApplicationModalState,
} from "./teamPublicDetailsModal.contract";

interface UseTeamPublicDetailsActionsParams {
  readonly companyId?: number;
  readonly setGigData: Dispatch<SetStateAction<CompanyGig | null>>;
}

interface UseTeamPublicDetailsActionsResult {
  readonly activeApplicationId: number | null;
  readonly isApplicationActionLoading: boolean;
  readonly applicationModalState: TeamPublicApplicationModalState;
  readonly handleOpenApplicationActionConfirm: (
    action: TeamPublicApplicationAction,
  ) => void;
  readonly handleCloseApplicationModal: () => void;
  readonly handleConfirmApplicationAction: (
    declineReason: string,
  ) => Promise<void>;
}

export const useTeamPublicDetailsActions = (
  params: UseTeamPublicDetailsActionsParams,
): UseTeamPublicDetailsActionsResult => {
  const [activeApplicationId, setActiveApplicationId] = useState<number | null>(
    null,
  );
  const [applicationModalState, setApplicationModalState] =
    useState<TeamPublicApplicationModalState>({ type: "closed" });
  const [isApplicationActionLoading, setIsApplicationActionLoading] =
    useState(false);
  const isApplicationActionInFlightRef = useRef(false);

  const handleApplicationStatusChange = async (
    action: TeamPublicApplicationAction,
    declineReason: string,
  ): Promise<boolean> => {
    const applicationId = action.applicationId;
    if (
      !params.companyId ||
      activeApplicationId !== null ||
      isApplicationActionInFlightRef.current
    ) {
      return false;
    }
    const api = Api();
    isApplicationActionInFlightRef.current = true;
    setActiveApplicationId(applicationId);
    try {
      const applicationResponse =
        await api.company.application.getApplicationById(
          params.companyId,
          applicationId,
        );
      const statusUpdate =
        action.type === "withdrawOffer"
          ? {
              status: ApplicationStatus.CLOSED,
              resolution: ApplicationResolution.WITHDRAWN,
            }
          : toApplicationStatusUpdate(
              action.type === "assign"
                ? ApplicationStatus.OFFERED
                : ApplicationStatus.CLOSED,
            );
      const payload = {
        ...applicationResponse.data,
        ...statusUpdate,
        ...(action.type === "decline" && {
          declineReason,
        }),
      };
      await api.company.application.updateApplicationStatus(
        params.companyId,
        applicationId,
        payload,
      );
      params.setGigData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          applications: prev.applications.map((application) =>
            application.id === applicationId
              ? {
                  ...application,
                  ...statusUpdate,
                  ...(action.type === "decline" && { declineReason }),
                }
              : application,
          ),
        };
      });
      return true;
    } catch {
      setApplicationModalState({ type: "error" });
      return false;
    } finally {
      isApplicationActionInFlightRef.current = false;
      setActiveApplicationId(null);
    }
  };

  const handleOpenApplicationActionConfirm = (
    action: TeamPublicApplicationAction,
  ): void => {
    setApplicationModalState({
      type: "confirm",
      action,
    });
  };

  const handleCloseApplicationModal = (): void => {
    if (isApplicationActionLoading) return;
    setApplicationModalState({ type: "closed" });
  };

  const handleConfirmApplicationAction = async (
    declineReason: string,
  ): Promise<void> => {
    if (
      applicationModalState.type !== "confirm" ||
      isApplicationActionLoading
    ) {
      return;
    }
    setIsApplicationActionLoading(true);
    const isSuccess = await handleApplicationStatusChange(
      applicationModalState.action,
      declineReason,
    );
    setIsApplicationActionLoading(false);
    if (isSuccess) {
      setApplicationModalState({ type: "closed" });
    }
  };

  return {
    activeApplicationId,
    isApplicationActionLoading,
    applicationModalState,
    handleOpenApplicationActionConfirm,
    handleCloseApplicationModal,
    handleConfirmApplicationAction,
  };
};
